import type { ClientSSOContext, SSO_CLIENT } from './types'

const SSO_KEYS = {
  CONTEXT: 'sso_context',
  STATE_NONCE: 'sso_state_nonce',
  STATE_CLIENT: 'sso_state_client',
  REDIRECT: 'sso_redirect_after_login',
  PENDING_FRESH_LOGIN: 'sso_pending_fresh_login',
  /** The one key here that lives in sessionStorage -- see below. */
  PENDING_LOGOUT_REDIRECT: 'sso_pending_logout_redirect',
  LAST_CLIENT: 'sso_last_client',
} as const

// Pre-rename key that persists in the browsers of already logged-in SSO
// users. Read it as a fallback so those sessions keep their SSO context
// (losing it would degrade client resolution to the ambiguous clientId
// lookup). Remove once all active sessions have rolled over.
const LEGACY_CONTEXT_KEY = 'clientSSOContext'
// Pre-rename redirect key; same migration treatment as the context key.
const LEGACY_REDIRECT_KEY = 'redirectAfterLogin'

// The Cognito logout hop (SSO landing -> /logout -> /login -> SSO landing)
// is a few redirects; anything older than this is a stale leftover.
const PENDING_FRESH_LOGIN_TTL_MS = 2 * 60 * 1000

interface PendingFreshLogin {
  clientName: string
  search: string
  createdAt: number
}

const PENDING_LOGOUT_REDIRECT_TTL_MS = 2 * 60 * 1000

interface PendingLogoutRedirect {
  clientName?: SSO_CLIENT
  /** True when the logout went through Cognito /logout, so that hop is done. */
  cognitoSessionCleared: boolean
  createdAt: number
}

/**
 * The logout hand-off belongs to the one tab that started the Cognito hop, and
 * that same tab is the one Cognito returns to -- so it lives in sessionStorage,
 * which is per tab and survives a same-tab navigation. In localStorage any
 * other tab that happened to load the sign-out landing could consume the
 * record first and leave the redirecting tab on a bare /logged-out, with no
 * idea which SSO client to offer a way back into.
 *
 * Access is guarded because storage throws outright in some private or
 * locked-down browsers. Losing the hand-off only costs the ?source= on the
 * landing page, so failing quietly beats breaking the sign-out.
 */
const readSession = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

const writeSession = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    // Storage unavailable - no-op.
  }
}

const removeSession = (key: string): void => {
  try {
    sessionStorage.removeItem(key)
  } catch {
    // Storage unavailable - no-op.
  }
}

/** Shared by the peeking and the consuming read so both agree on what counts
 * as a live hand-off -- an expired one must not steer either. */
const parsePendingLogoutRedirect = (raw: string | null): PendingLogoutRedirect | null => {
  if (!raw) return null
  try {
    const pending: PendingLogoutRedirect = JSON.parse(raw)
    if (
      typeof pending?.createdAt !== 'number' ||
      Date.now() - pending.createdAt > PENDING_LOGOUT_REDIRECT_TTL_MS
    ) {
      return null
    }
    return pending
  } catch {
    return null
  }
}

export const ssoStorage = {
  getContext(): ClientSSOContext | null {
    try {
      const raw = localStorage.getItem(SSO_KEYS.CONTEXT) ?? localStorage.getItem(LEGACY_CONTEXT_KEY)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  },

  setContext(ctx: ClientSSOContext): void {
    localStorage.setItem(SSO_KEYS.CONTEXT, JSON.stringify(ctx))
    localStorage.removeItem(LEGACY_CONTEXT_KEY)
  },

  mergeContext(partial: Partial<ClientSSOContext>): void {
    const existing = this.getContext()
    this.setContext({ ...(existing ?? {}), ...partial })
  },

  /**
   * The SSO client of the most recent landing, kept apart from the context.
   *
   * The context is rich and is rewritten by several steps of the sign-in; this
   * is one string, written once at /sso/:clientName, where the client is known
   * for certain. Logout reads it as the last resort so the logged-out page can
   * still offer the right way back in.
   */
  setLastClientName(clientName: SSO_CLIENT): void {
    localStorage.setItem(SSO_KEYS.LAST_CLIENT, clientName)
  },

  getLastClientName(): SSO_CLIENT | undefined {
    return (localStorage.getItem(SSO_KEYS.LAST_CLIENT) as SSO_CLIENT | null) ?? undefined
  },

  /** Deliberately leaves LAST_CLIENT alone -- see setLastClientName. */
  clearContext(): void {
    localStorage.removeItem(SSO_KEYS.CONTEXT)
    localStorage.removeItem(LEGACY_CONTEXT_KEY)
  },

  getOAuthState(): { nonce: string; clientName: string } | null {
    const nonce = localStorage.getItem(SSO_KEYS.STATE_NONCE)
    const clientName = localStorage.getItem(SSO_KEYS.STATE_CLIENT)
    if (!nonce || !clientName) return null
    return { nonce, clientName }
  },

  setStateNonce(nonce: string, clientName: string): void {
    localStorage.setItem(SSO_KEYS.STATE_NONCE, nonce)
    localStorage.setItem(SSO_KEYS.STATE_CLIENT, clientName)
  },

  clearStateNonce(): void {
    localStorage.removeItem(SSO_KEYS.STATE_NONCE)
    localStorage.removeItem(SSO_KEYS.STATE_CLIENT)
  },

  setRedirectUrl(url: string): void {
    localStorage.setItem(SSO_KEYS.REDIRECT, url)
    localStorage.removeItem(LEGACY_REDIRECT_KEY)
  },

  getRedirectUrl(): string | null {
    return localStorage.getItem(SSO_KEYS.REDIRECT) ?? localStorage.getItem(LEGACY_REDIRECT_KEY)
  },

  setPendingFreshLogin(clientName: string, search: string): void {
    const pending: PendingFreshLogin = { clientName, search, createdAt: Date.now() }
    localStorage.setItem(SSO_KEYS.PENDING_FRESH_LOGIN, JSON.stringify(pending))
  },

  /** Reads without consuming, so a caller can tell a redirect tail from a
   * normal load before deciding to act on it. */
  peekPendingFreshLogin(): PendingFreshLogin | null {
    try {
      const raw = localStorage.getItem(SSO_KEYS.PENDING_FRESH_LOGIN)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  },

  getAndClearPendingFreshLogin(): PendingFreshLogin | null {
    try {
      const raw = localStorage.getItem(SSO_KEYS.PENDING_FRESH_LOGIN)
      if (!raw) return null
      localStorage.removeItem(SSO_KEYS.PENDING_FRESH_LOGIN)
      const pending: PendingFreshLogin = JSON.parse(raw)
      if (
        typeof pending.clientName !== 'string' ||
        !pending.clientName ||
        typeof pending.search !== 'string' ||
        typeof pending.createdAt !== 'number' ||
        Date.now() - pending.createdAt > PENDING_FRESH_LOGIN_TTL_MS
      ) {
        return null
      }
      return pending
    } catch {
      return null
    }
  },

  getAndClearRedirectUrl(): string | null {
    const url = this.getRedirectUrl()
    localStorage.removeItem(SSO_KEYS.REDIRECT)
    localStorage.removeItem(LEGACY_REDIRECT_KEY)
    return url
  },

  /**
   * Ordering against the logout wipe no longer matters: this is sessionStorage
   * and the wipe only clears localStorage.
   *
   * A second logout overwrites this blindly rather than guarding against a
   * downgrade. It does not need to: useLogout peeks at this record and carries
   * the client name forward before it writes, so the second write is the same
   * name the first one recorded.
   */
  setPendingLogoutRedirect(
    clientName: SSO_CLIENT | undefined,
    cognitoSessionCleared: boolean
  ): void {
    const pending: PendingLogoutRedirect = {
      clientName,
      cognitoSessionCleared,
      createdAt: Date.now(),
    }
    writeSession(SSO_KEYS.PENDING_LOGOUT_REDIRECT, JSON.stringify(pending))
  },

  /** Reads the hand-off without consuming it, so a repeat logout can carry
   * forward a client name the first pass already recorded. */
  peekPendingLogoutRedirect(): PendingLogoutRedirect | null {
    return parsePendingLogoutRedirect(readSession(SSO_KEYS.PENDING_LOGOUT_REDIRECT))
  },

  getAndClearPendingLogoutRedirect(): PendingLogoutRedirect | null {
    const pending = parsePendingLogoutRedirect(readSession(SSO_KEYS.PENDING_LOGOUT_REDIRECT))
    // Cleared even when it failed to parse or had expired: a record this read
    // will not act on is a record nothing else should trip over either.
    removeSession(SSO_KEYS.PENDING_LOGOUT_REDIRECT)
    return pending
  },
}
